import React, { forwardRef, useImperativeHandle } from "react";
import {
  Form,
  Input,
  Button,
  AutoComplete,
  Row,
  Col,
  FormInstance,
} from "antd";
import styles from "./CreditCardForm.module.scss";
import moment from "moment";
import { useCreditCardForm } from "../../../hooks/useCreditCardForm";
import {
  CreditCardFormRef,
  IsValidCCStateType,
} from "../../../hooks/useCreditCardActions";
import { InputMask } from "@mtm/components";

type Props = {
  form: FormInstance;
  isModalLayout?: boolean;
  isValidCCState: IsValidCCStateType;
  validateAndCheckForm: Function;
};

const CreditCardForm = forwardRef<CreditCardFormRef, Props>((props, ref) => {
  const { form, isModalLayout, isValidCCState, validateAndCheckForm } = props;

  const {
    autoCompleteOptions,
    manualAddressEntry,
    billingAddress,
    handleCantFindAddress,
    handleAddressSelect,
    handleAddressSearch,
    handleAddressReset,
    handleInputNameChange,
  } = useCreditCardForm(form);

  useImperativeHandle(ref, () => ({ handleAddressReset }));

  const handleValuesChange = () => validateAndCheckForm();

  const displayInvalidMessage = () => {
    return !isValidCCState.value ? (
      <Row justify="center" gutter={[0, 15]}>
        <Col span={24} />
        <Col className={styles.invalidCC}>
          There was an error processing this credit card.
        </Col>
      </Row>
    ) : (
      ""
    );
  };

  const handleFieldsChange = () => isValidCCState.set(true);

  return (
    <>
      <Form
        name="credit_card"
        layout="vertical"
        form={form}
        onValuesChange={handleValuesChange}
        onFieldsChange={handleFieldsChange}
      >
        <Row className={styles.mobileFormViewToggle}>
          <Col lg={isModalLayout ? 0 : 4} />
          <Col span={24} lg={isModalLayout ? 12 : 8}>
            <Row>
              <Col span={24}>
                <Form.Item
                  name="firstName"
                  label={
                    <div className={styles.creditInputLabels}>First Name</div>
                  }
                >
                  <Input
                    maxLength={100}
                    placeholder="Enter cardholder’s first name"
                    className={`${styles.cardHoldersNameInp} ${styles.inpBorder}`}
                    onChange={(e) => handleInputNameChange(e, "firstName")}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="middleName"
                  label={
                    <div className={styles.creditInputLabels}>Middle Name</div>
                  }
                >
                  <div>
                    <Input
                      maxLength={100}
                      placeholder="Enter cardholder’s middle name"
                      className={`${styles.cardHoldersNameInp} ${styles.inpBorder}`}
                      onChange={(e) => handleInputNameChange(e, "middleName")}
                    />
                    <div className={styles.optionalField}>**Optional</div>
                  </div>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="lastName"
                  label={
                    <div className={styles.creditInputLabels}>Last Name</div>
                  }
                >
                  <Input
                    maxLength={100}
                    placeholder="Enter cardholder’s last name"
                    className={`${styles.cardHoldersNameInp} ${styles.inpBorder}`}
                    onChange={(e) => handleInputNameChange(e, "lastName")}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="cardNumber"
                  label={
                    <div className={styles.creditInputLabels}>Card Number</div>
                  }
                  rules={[
                    {
                      pattern: /^(?:\d\s*){13,16}$/,
                      message: "Please Enter a 13-16 Digit Card Number",
                    },
                  ]}
                  validateTrigger={["onBlur"]}
                >
                  <InputMask
                    className={`${styles.cardHoldersNameInp} ${styles.inpBorder}`}
                    mask="9999 9999 9999 9999"
                    maskPlaceholder=""
                    placeholder="0000 0000 0000 0000"
                    onChange={(value: any) => {
                      form.setFieldsValue({ cardNumber: value });
                      form.validateFields(["cardNumber"]);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[40, 0]}>
              <Col>
                <Form.Item
                  name="expiryDate"
                  label={
                    <div className={styles.creditInputLabels}>
                      Expiration Date
                    </div>
                  }
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (!value) {
                          return Promise.resolve();
                        }
                        if (!/^(0[1-9]|1[0-2])\/([0-9]{2})$/.test(value)) {
                          return Promise.reject(new Error("Invalid Date"));
                        }

                        const [expMonth, expYear] = value.split("/");
                        const expiryDate = moment(
                          `${expMonth}/01/${expYear}`,
                          "MM/DD/YY"
                        );
                        const currentDate = moment().startOf("month");

                        if (expiryDate.isBefore(currentDate)) {
                          return Promise.reject(new Error("Date is too old"));
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputMask
                    className={`${styles.expirationDateInp} ${styles.inpBorder}`}
                    mask="99/99"
                    maskPlaceholder=""
                    placeholder="MM/YY"
                    onChange={(value: any) => {
                      form.setFieldsValue({ expiryDate: value });
                      form.validateFields(["expiryDate"]);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  name="cvv"
                  label={<div className={styles.creditInputLabels}>CVV</div>}
                  rules={[
                    {
                      min: 3,
                      message: "Enter 3-4 Digit CVV",
                    },
                  ]}
                >
                  <Input.Password
                    type="tel"
                    placeholder="***"
                    minLength={3}
                    maxLength={4}
                    className={`${styles.cvvInp} ${styles.inpBorder}`}
                    onChange={(event) => {
                      let value = event.target.value;
                      value = value.replace(/[^\d]/g, "");
                      form.setFieldsValue({ cvv: value });
                      form.validateFields(["cvv"]);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24} lg={isModalLayout ? 12 : 8}>
            {!billingAddress.address1 && !manualAddressEntry ? (
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="billingAddress"
                    label={
                      <div className={styles.creditInputLabels}>
                        Billing Address
                      </div>
                    }
                  >
                    <Row>
                      <Col span={24}>
                        <AutoComplete
                          className={styles.billingAddressInp}
                          options={autoCompleteOptions}
                          onSearch={handleAddressSearch}
                          onSelect={handleAddressSelect}
                          placeholder="Enter street address"
                        />
                      </Col>
                      <Col span={24}>
                        <Button
                          type="link"
                          id={styles.cantFindAddressBtn}
                          onClick={handleCantFindAddress}
                        >
                          Can't find your address?
                        </Button>
                      </Col>
                    </Row>
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <>
                <Form.Item
                  name="address1"
                  label={
                    <div className={styles.creditInputLabels}>Address 1</div>
                  }
                >
                  <Input
                    placeholder="Street Address"
                    className={`${styles.address1Inp} ${styles.inpBorder}`}
                  />
                </Form.Item>

                <Form.Item
                  name="address2"
                  label={
                    <div className={styles.creditInputLabels}>Address 2</div>
                  }
                >
                  <Input
                    placeholder="Suite#, Apt#, PO Box, etc"
                    className={`${styles.address2Inp} ${styles.inpBorder}`}
                  />
                </Form.Item>

                <Row gutter={{ xs: 10, sm: 10, md: 10, lg: 20 }}>
                  <Col>
                    <Form.Item
                      name="city"
                      label={
                        <div className={styles.creditInputLabels}>City</div>
                      }
                    >
                      <Input
                        value={billingAddress.city}
                        placeholder="San Diego"
                        className={`${styles.cityInp} ${styles.inpBorder}`}
                      />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Form.Item
                      name="state"
                      label={
                        <div className={styles.creditInputLabels}>State</div>
                      }
                    >
                      <Input
                        value={billingAddress.state}
                        placeholder="CA"
                        className={`${styles.stateInp} ${styles.inpBorder}`}
                      />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Form.Item
                      name="zipcode"
                      label={
                        <div className={styles.creditInputLabels}>Zip Code</div>
                      }
                    >
                      <Input
                        value={billingAddress.zipcode}
                        placeholder="xxxxx"
                        className={`${styles.zipCodeInp} ${styles.inpBorder}`}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className={styles.resetBtnContainer}>
                  <Button
                    type="link"
                    id={styles.resetAddressBtn}
                    onClick={handleAddressReset}
                  >
                    Reset Address Lookup
                  </Button>
                </Row>
              </>
            )}
          </Col>
          <Col lg={isModalLayout ? 0 : 4} />
        </Row>

        {displayInvalidMessage()}
      </Form>
    </>
  );
});

export default React.memo(CreditCardForm);
